/**
 * WebGL2 instanced renderer for the swarm.
 *
 * One draw call for the whole crowd. A single quad is uploaded once; per-agent
 * position, heading and tint live in a second buffer that is re-uploaded each
 * frame with `bufferSubData` over a pre-sized allocation — the same shape as
 * the game's instanced render path, where a job fills a transform buffer and
 * the GPU consumes it in chunks.
 *
 * The instance attribute layout is deliberately one interleaved stride so the
 * upload is a single contiguous write:
 *
 *   [ x, y, dirX, dirY, tint ]  — 5 floats, 20 bytes
 */

const VERT = `#version 300 es
precision highp float;

layout(location = 0) in vec2 aCorner;    // unit quad, -0.5 .. 0.5
layout(location = 1) in vec2 aPos;       // world position
layout(location = 2) in vec2 aDir;       // unit heading
layout(location = 3) in float aTint;     // 0..1 palette position

uniform vec2  uViewport;    // world units
uniform float uSize;        // agent size, world units
uniform float uSprite;      // 1.0 when drawing the sprite atlas

out float vTint;
out vec2  vLocal;
out vec2  vUV;

void main() {
  vec2 world;

  if (uSprite > 0.5) {
    // Characters are drawn three-quarter view, so they stay upright and only
    // mirror by travel direction. Rotating them with the heading looks wrong.
    float flip = aDir.x < 0.0 ? -1.0 : 1.0;
    vec2 local = aCorner * vec2(uSize, uSize);
    world = aPos + vec2(local.x, local.y);
    // World y already runs downward and the atlas is uploaded without
    // UNPACK_FLIP_Y_WEBGL, so v = 0 is the top of the image and the corner
    // maps straight across. Negating here stands every sprite on its head.
    vUV = vec2(aCorner.x * flip, aCorner.y) + 0.5;
  } else {
    // Orient the quad along the heading so the crowd reads as directional.
    vec2 fwd = length(aDir) > 0.001 ? normalize(aDir) : vec2(0.0, 1.0);
    vec2 side = vec2(-fwd.y, fwd.x);
    vec2 local = aCorner * vec2(uSize * 0.78, uSize * 1.25);
    world = aPos + side * local.x + fwd * local.y;
    vUV = vec2(0.0);
  }

  vec2 clip = (world / uViewport) * 2.0 - 1.0;
  gl_Position = vec4(clip.x, -clip.y, 0.0, 1.0);

  vTint = aTint;
  vLocal = aCorner * 2.0;
}`;

const FRAG = `#version 300 es
precision mediump float;

in float vTint;
in vec2  vLocal;
in vec2  vUV;

uniform vec3      uColorA;
uniform vec3      uColorB;
uniform float     uAlpha;
// Shared with the vertex stage, so the precision has to match it explicitly:
// the two stages default to highp and mediump respectively, and GLSL ES 3.0
// refuses to link a uniform whose precision differs across them.
uniform highp float uSprite;
uniform sampler2D uAtlas;

out vec4 outColor;

void main() {
  if (uSprite > 0.5) {
    // Four variants in a 2x2 atlas, picked by the per-instance tint so the
    // horde is mixed without a second attribute.
    float idx = floor(clamp(vTint, 0.0, 0.999) * 4.0);
    vec2 cell = vec2(mod(idx, 2.0), floor(idx / 2.0));
    vec2 uv = (clamp(vUV, 0.0, 1.0) + cell) * 0.5;
    vec4 tex = texture(uAtlas, uv);
    if (tex.a <= 0.02) discard;
    outColor = vec4(tex.rgb, tex.a * uAlpha);
  } else {
    // Soft capsule falloff, cheap: radial on the unit quad.
    float d = length(vLocal);
    float a = smoothstep(1.0, 0.35, d);
    if (a <= 0.01) discard;
    vec3 c = mix(uColorA, uColorB, vTint);
    outColor = vec4(c, a * uAlpha);
  }
}`;

const FLOATS_PER_INSTANCE = 5;

function compile(gl: WebGL2RenderingContext, type: number, src: string) {
  const sh = gl.createShader(type);
  if (!sh) throw new Error("createShader failed");
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(sh);
    gl.deleteShader(sh);
    throw new Error(`shader compile failed: ${log}`);
  }
  return sh;
}

export type Palette = {
  /** rgb 0..1 */
  a: [number, number, number];
  b: [number, number, number];
  alpha: number;
};

export class SwarmRenderer {
  private readonly gl: WebGL2RenderingContext;
  private readonly program: WebGLProgram;
  private readonly vao: WebGLVertexArrayObject;
  private readonly instanceBuffer: WebGLBuffer;
  private readonly quadBuffer: WebGLBuffer;
  private readonly data: Float32Array;
  private readonly uViewport: WebGLUniformLocation | null;
  private readonly uSize: WebGLUniformLocation | null;
  private readonly uColorA: WebGLUniformLocation | null;
  private readonly uColorB: WebGLUniformLocation | null;
  private readonly uAlpha: WebGLUniformLocation | null;
  private readonly uSprite: WebGLUniformLocation | null;
  private readonly uAtlas: WebGLUniformLocation | null;
  private atlas: WebGLTexture | null = null;

  constructor(gl: WebGL2RenderingContext, maxInstances: number) {
    this.gl = gl;
    this.data = new Float32Array(maxInstances * FLOATS_PER_INSTANCE);

    const vs = compile(gl, gl.VERTEX_SHADER, VERT);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    const program = gl.createProgram();
    if (!program) throw new Error("createProgram failed");
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const log = gl.getProgramInfoLog(program);
      gl.deleteProgram(program);
      throw new Error(`program link failed: ${log}`);
    }
    this.program = program;

    const vao = gl.createVertexArray();
    const quadBuffer = gl.createBuffer();
    const instanceBuffer = gl.createBuffer();
    if (!vao || !quadBuffer || !instanceBuffer)
      throw new Error("buffer allocation failed");
    this.vao = vao;
    this.quadBuffer = quadBuffer;
    this.instanceBuffer = instanceBuffer;

    gl.bindVertexArray(vao);

    // Static unit quad as a triangle strip.
    gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-0.5, -0.5, 0.5, -0.5, -0.5, 0.5, 0.5, 0.5]),
      gl.STATIC_DRAW,
    );
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    // Per-instance stream, sized once and refilled every frame.
    gl.bindBuffer(gl.ARRAY_BUFFER, instanceBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, this.data.byteLength, gl.DYNAMIC_DRAW);
    const stride = FLOATS_PER_INSTANCE * 4;
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 2, gl.FLOAT, false, stride, 0);
    gl.vertexAttribDivisor(1, 1);
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 2, gl.FLOAT, false, stride, 8);
    gl.vertexAttribDivisor(2, 1);
    gl.enableVertexAttribArray(3);
    gl.vertexAttribPointer(3, 1, gl.FLOAT, false, stride, 16);
    gl.vertexAttribDivisor(3, 1);

    gl.bindVertexArray(null);

    this.uViewport = gl.getUniformLocation(program, "uViewport");
    this.uSize = gl.getUniformLocation(program, "uSize");
    this.uColorA = gl.getUniformLocation(program, "uColorA");
    this.uColorB = gl.getUniformLocation(program, "uColorB");
    this.uAlpha = gl.getUniformLocation(program, "uAlpha");
    this.uSprite = gl.getUniformLocation(program, "uSprite");
    this.uAtlas = gl.getUniformLocation(program, "uAtlas");
  }

  /**
   * Upload the agent sprite atlas. A 2x2 sheet of the game's own enemy art;
   * the fragment shader picks a cell from the per-instance tint.
   */
  setAtlas(image: TexImageSource) {
    const gl = this.gl;
    const tex = gl.createTexture();
    if (!tex) return;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
    // No mips: cells would bleed into each other across the atlas seams.
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.bindTexture(gl.TEXTURE_2D, null);
    this.atlas = tex;
  }

  get hasAtlas() {
    return this.atlas !== null;
  }

  /** Pack agent state into the interleaved instance stream. */
  fill(
    count: number,
    px: Float32Array,
    py: Float32Array,
    vx: Float32Array,
    vy: Float32Array,
    tint: Float32Array,
  ) {
    const d = this.data;
    for (let i = 0, o = 0; i < count; i++, o += FLOATS_PER_INSTANCE) {
      d[o] = px[i];
      d[o + 1] = py[i];
      d[o + 2] = vx[i];
      d[o + 3] = vy[i];
      d[o + 4] = tint[i];
    }
  }

  draw(
    count: number,
    worldW: number,
    worldH: number,
    agentSize: number,
    palette: Palette,
    sprite = false,
  ) {
    const gl = this.gl;
    if (count <= 0) return;

    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuffer);
    gl.bufferSubData(
      gl.ARRAY_BUFFER,
      0,
      this.data.subarray(0, count * FLOATS_PER_INSTANCE),
    );

    gl.useProgram(this.program);
    gl.uniform2f(this.uViewport, worldW, worldH);
    gl.uniform1f(this.uSize, agentSize);
    gl.uniform3f(this.uColorA, palette.a[0], palette.a[1], palette.a[2]);
    gl.uniform3f(this.uColorB, palette.b[0], palette.b[1], palette.b[2]);
    gl.uniform1f(this.uAlpha, palette.alpha);

    const useSprite = sprite && this.atlas !== null;
    gl.uniform1f(this.uSprite, useSprite ? 1 : 0);
    if (useSprite) {
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this.atlas);
      gl.uniform1i(this.uAtlas, 0);
    }

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, count);
    gl.bindVertexArray(null);
  }

  dispose() {
    const gl = this.gl;
    if (this.atlas) gl.deleteTexture(this.atlas);
    gl.deleteBuffer(this.instanceBuffer);
    gl.deleteBuffer(this.quadBuffer);
    gl.deleteVertexArray(this.vao);
    gl.deleteProgram(this.program);
  }
}
